/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Data hooks + shared context for the Billing section. Query keys match the
// ones the old Wallet / Invoices pages used so invalidations elsewhere in the
// app ('wallet', 'transactions', 'invoices', 'paymentMethods') still land.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { billingDetailsService, currencyService, invoiceService, walletService } from '../../services/api';
import type {
  BillingDetailsResponse,
  Invoice,
  SavedPaymentMethod,
  SpendByProjectResponse,
  Transaction,
  TransactionGroup,
  TransactionQuery,
  TransactionSummary,
  UsageByProductResponse,
  UserExpenditureSummary,
} from '../../types';
import { loadRazorpayScript } from '../../utils/razorpay';
import { DEFAULT_USD_TO_INR, DisplayCurrency } from './billingUtils';

export interface BillingContextValue {
  currency: DisplayCurrency;
  setCurrency: (c: DisplayCurrency) => void;
  exchangeRate: number;
}

/** Currency toggle + exchange rate provided by BillingLayout's <Outlet context>. */
export const useBillingContext = () => useOutletContext<BillingContextValue>();

export const TX_PAGE_SIZE = 100; // backend caps page_size at 100

export const useTransactions = (page: number) =>
  useQuery({
    queryKey: ['transactions', page, TX_PAGE_SIZE],
    queryFn: () =>
      walletService.getTransactions(page, TX_PAGE_SIZE).then(
        (res) => res.data as { transactions: Transaction[]; total: number; page: number; page_size: number },
      ),
    placeholderData: (prev) => prev,
  });

/** HTTP status of an axios error, if any. */
const statusOf = (err: unknown): number | undefined => (err as { response?: { status?: number } })?.response?.status;

/**
 * True when an optional endpoint isn't usable for this user/deployment:
 * missing on an older backend (404/405/501) or gated (403, e.g. KYC). Callers
 * hide the data instead of erroring.
 */
export const isUnavailable = (err: unknown) => [403, 404, 405, 501].includes(statusOf(err) ?? 0);

/** Run an optional request; resolve to null when the endpoint is unavailable. */
const optional = async <T,>(fn: () => Promise<{ data: T }>): Promise<T | null> => {
  try {
    return (await fn()).data;
  } catch (err) {
    if (isUnavailable(err)) return null;
    throw err;
  }
};

export interface FilteredTransactionsResult {
  transactions: Transaction[];
  total: number;
  summary?: TransactionSummary;
  /** Backend ignored the filters (pre-filter version): filter client-side. */
  legacy: boolean;
}

/**
 * GET /wallet/transactions with server-side filters + totals. An older
 * backend ignores unknown params and omits `summary`; that is reported as
 * `legacy` so the caller can filter the page itself.
 */
export const useFilteredTransactions = (query: TransactionQuery, enabled = true) =>
  useQuery({
    queryKey: ['transactions', 'filtered', query],
    queryFn: async (): Promise<FilteredTransactionsResult> => {
      const { data } = await walletService.getTransactionsFiltered({ ...query, include_summary: true });
      return {
        transactions: data.transactions || [],
        total: data.total || 0,
        summary: data.summary,
        legacy: !data.summary,
      };
    },
    enabled,
    placeholderData: (prev) => prev,
  });

export interface TransactionGroupsResult {
  groups: TransactionGroup[];
  total: number;
  summary?: TransactionSummary;
}

/** Server-side per-day/per-resource groups; data is null on older backends. */
export const useTransactionGroups = (query: TransactionQuery, enabled = true) =>
  useQuery({
    queryKey: ['transactions', 'grouped', query],
    queryFn: () =>
      optional(() => walletService.getTransactionGroups(query)).then((d): TransactionGroupsResult | null =>
        d ? { groups: d.groups || [], total: d.total || 0, summary: d.summary } : null,
      ),
    enabled,
    placeholderData: (prev) => prev,
  });

/** Month-to-date spend by product; data is null when unavailable. */
export const useUsageByProduct = () =>
  useQuery({
    queryKey: ['usageByProduct', 'month'],
    queryFn: () => optional<UsageByProductResponse>(() => walletService.getUsageByProduct({ period: 'month' })),
    staleTime: 60_000,
  });

/** Month-to-date spend per project; data is null when unavailable. */
export const useSpendByProject = () =>
  useQuery({
    queryKey: ['spendByProject', 'month'],
    queryFn: () => optional<SpendByProjectResponse>(() => walletService.getSpendByProject({ period: 'month' })),
    staleTime: 60_000,
    retry: false,
  });

/** Invoice billing details; data is null when the backend has no endpoint. */
export const useBillingDetails = () =>
  useQuery({
    queryKey: ['billingDetails'],
    queryFn: () => optional<BillingDetailsResponse>(() => billingDetailsService.get()),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const usePaymentMethods = () => {
  const q = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => walletService.getPaymentMethods().then((res) => res.data),
  });
  const cards: SavedPaymentMethod[] = q.data?.data || [];
  return { ...q, cards };
};

export const useInvoices = () => {
  const q = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoiceService.list(1, 100).then((res) => res.data),
  });
  const invoices: Invoice[] = q.data?.invoices || [];
  const total: number = q.data?.total ?? invoices.length;
  return { ...q, invoices, total };
};

export const useExpenditure = () =>
  useQuery({
    queryKey: ['expenditure'],
    queryFn: () => walletService.getExpenditure().then((res) => res.data as UserExpenditureSummary),
    staleTime: 60_000,
  });

export const useExchangeRate = () => {
  const { data } = useQuery({
    queryKey: ['currencySettings'],
    queryFn: () => currencyService.get().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
  return (data?.usd_to_inr as number) || DEFAULT_USD_TO_INR;
};

/** Open Razorpay checkout for an order the backend created (top-up or invoice). */
export const openRazorpayCheckout = async (
  order: any,
  onPaid: (resp: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => Promise<void>,
  extra: Record<string, unknown> = {},
) => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    toast.error('Failed to load payment gateway. Please try again.');
    return;
  }
  const razorpay = new window.Razorpay({
    key: order.key,
    amount: order.amount * 100,
    currency: order.currency,
    name: order.name,
    description: order.description,
    order_id: order.order_id,
    handler: onPaid,
    theme: { color: '#F97316' },
    ...extra,
  });
  razorpay.open();
};

/** Invalidate everything a wallet movement can change. */
export const useInvalidateWallet = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['wallet'] });
    qc.invalidateQueries({ queryKey: ['transactions'] });
    qc.invalidateQueries({ queryKey: ['expenditure'] });
    qc.invalidateQueries({ queryKey: ['usageByProduct'] });
    qc.invalidateQueries({ queryKey: ['spendByProject'] });
  };
};
